/**
 * DeepLinkLibrary.jsx (FEAT-DEEPLINK-001) — Content → Deep Link Library.
 *
 * A standalone store of a tenant's deep links (create / edit / archive / delete /
 * CSV import-export). Other screens pull from it via <DeepLinkPicker/>; this page
 * is the authoring surface. Rendered under <PermissionRoute group=
 * "content/deep-link-library" slug="deep-link-list">, so it renders its own Header
 * and reads the already-fetched permission list from PermissionRouteContext.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Flex, HStack, Button, IconButton, Text, Input, InputGroup, InputLeftElement,
  Select, Table, Thead, Tbody, Tr, Th, Td, TableContainer, Badge, Tooltip, Spinner,
  Center, useDisclosure, useToast, Menu, MenuButton, MenuList, MenuItem,
} from '@chakra-ui/react';
import {
  FiPlus, FiSearch, FiEdit2, FiTrash2, FiArchive, FiRotateCcw, FiUploadCloud,
  FiDownload, FiMoreVertical, FiLink,
} from 'react-icons/fi';
import Pagination from 'react-js-pagination';
import BoxWrapper from '../../components/BoxWrapper/BoxWrapper';
import Header from '../../components/Header';
import ConfirmDialog from '../../components/ConfirmDialog';
import { usePermissionRouteContext } from '../../components/PermissionRoute';
import usePagePermissions from '../../hooks/usePagePermissions';
import { hasPermission } from '../../utils/DefaultFunction';
import deepLinksApi, { downloadBlob } from '../../api/deepLinks';
import DeepLinkModal from './DeepLinkModal';
import CsvImportModal from './CsvImportModal';
import PageHeading from '../../components/shared/PageHeading';
import { LuLink } from 'react-icons/lu';

const LIMIT = 20;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—');

function LinkCell({ value }) {
  if (!value) return <Text color="gray.300">—</Text>;
  return (
    <Tooltip label={value} openDelay={400}>
      <Text fontFamily="mono" fontSize="12px" color="gray.600" maxW="220px" isTruncated>{value}</Text>
    </Tooltip>
  );
}

export default function DeepLinkLibrary() {
  const toast = useToast();
  const routeCtx = usePermissionRouteContext();
  // Avoid a duplicate fetch when under PermissionRoute (it already has the list).
  const fallback = usePagePermissions(routeCtx ? null : 'content/deep-link-library');
  const can = useCallback(
    (slug) => (routeCtx ? hasPermission(slug, routeCtx.permissions) : fallback.can(slug)),
    [routeCtx, fallback],
  );

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // '' all | '1' active | '0' archived
  const [categoryFilter, setCategoryFilter] = useState('');

  const drawer = useDisclosure();
  const [editing, setEditing] = useState(null);
  const csv = useDisclosure();
  const del = useDisclosure();
  const [target, setTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: LIMIT };
      if (query.trim()) params.query = query.trim();
      if (statusFilter !== '') params.status_id = statusFilter;
      if (categoryFilter) params.category = categoryFilter;
      const res = await deepLinksApi.list(params);
      setRows(res.data.data || []);
      setTotal(res.data.pagination?.total || 0);
    } catch (e) {
      toast({ title: 'Could not load deep links', description: e?.response?.data?.error || e.message, status: 'error' });
      setRows([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, query, statusFilter, categoryFilter, toast]);

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);

  // Distinct categories from the current page, for the filter dropdown.
  const categories = useMemo(
    () => Array.from(new Set(rows.map((r) => r.category).filter(Boolean)))
      .sort((a, b) => String(a).localeCompare(String(b))),
    [rows]
  );

  const openNew = () => { setEditing(null); drawer.onOpen(); };
  const openEdit = (row) => { setEditing(row); drawer.onOpen(); };
  const askDelete = (row) => { setTarget(row); del.onOpen(); };

  const toggleStatus = async (row) => {
    try {
      await deepLinksApi.setStatus(row.id, row.status_id === 0 ? 1 : 0);
      toast({ title: row.status_id === 0 ? 'Restored' : 'Archived', status: 'success' });
      load();
    } catch (e) {
      toast({ title: 'Could not change status', description: e?.response?.data?.error || e.message, status: 'error' });
    }
  };

  const doDelete = async () => {
    try {
      await deepLinksApi.remove(target.id);
      toast({ title: 'Deep link deleted', status: 'success' });
      load();
    } catch (e) {
      toast({ title: 'Could not delete', description: e?.response?.data?.error || e.message, status: 'error' });
    }
  };

  const doExport = async () => {
    try { const res = await deepLinksApi.exportCsv(); downloadBlob(res.data, 'deep-links.csv'); }
    catch (e) { toast({ title: 'Export failed', status: 'error' }); }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <>
      <Header />
      <BoxWrapper>
        <PageHeading
          icon={LuLink}
          title="Deep Link Library"
          subtitle="Keep your app deep links in one place, then pull them into Push, In-App, Email and Smart Links."
          mb={1}
        >
          <HStack>
            <Button leftIcon={<FiDownload />} size="sm" variant="outline" onClick={doExport}>Export</Button>
            {can('deep-link-create') && (
              <Button leftIcon={<FiUploadCloud />} size="sm" variant="outline" onClick={csv.onOpen}>Upload CSV</Button>
            )}
            {can('deep-link-create') && (
              <Button leftIcon={<FiPlus />} size="sm" colorScheme="brand" onClick={openNew}>New deep link</Button>
            )}
          </HStack>
        </PageHeading>

        <Flex gap={3} wrap="wrap" my={4}>
          <InputGroup maxW="320px" size="sm">
            <InputLeftElement pointerEvents="none"><FiSearch color="#9aa0a6" /></InputLeftElement>
            <Input value={query} onChange={(e) => { setPage(1); setQuery(e.target.value); }} placeholder="Search name or category…" borderRadius="8px" />
          </InputGroup>
          <Select size="sm" maxW="170px" value={statusFilter} onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }} borderRadius="8px">
            <option value="">All statuses</option>
            <option value="1">Active</option>
            <option value="0">Archived</option>
          </Select>
          {categories.length > 0 && (
            <Select size="sm" maxW="180px" value={categoryFilter} onChange={(e) => { setPage(1); setCategoryFilter(e.target.value); }} borderRadius="8px">
              <option value="">All categories</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
          )}
        </Flex>

        <TableContainer borderWidth="1px" borderColor="#ebedf0" borderRadius="12px">
          <Table variant="simple" size="sm">
            <Thead bg="gray.50">
              <Tr>
                <Th>Name</Th><Th>Category</Th><Th>Android</Th><Th>iOS</Th><Th>Status</Th><Th>Updated</Th><Th textAlign="right">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {loading ? (
                <Tr><Td colSpan={7}><Center py={10}><Spinner /></Center></Td></Tr>
              ) : rows.length === 0 ? (
                <Tr><Td colSpan={7}>
                  <Center py={12}>
                    <Box textAlign="center" color="gray.400">
                      <FiLink size={26} style={{ margin: '0 auto 8px' }} />
                      <Text fontSize="sm">No deep links yet. Create one or import a CSV.</Text>
                    </Box>
                  </Center>
                </Td></Tr>
              ) : rows.map((row) => (
                <Tr key={row.id} _hover={{ bg: 'gray.50' }}>
                  <Td fontWeight={600}>{row.name}</Td>
                  <Td>{row.category ? <Badge colorScheme="gray">{row.category}</Badge> : <Text color="gray.300">—</Text>}</Td>
                  <Td><LinkCell value={row.android_link} /></Td>
                  <Td><LinkCell value={row.ios_link} /></Td>
                  <Td>
                    <Badge colorScheme={row.status_id === 0 ? 'orange' : 'green'} variant="subtle">
                      {row.status_id === 0 ? 'Archived' : 'Active'}
                    </Badge>
                  </Td>
                  <Td color="gray.500" fontSize="12px">{fmtDate(row.updated_at || row.updatedAt)}</Td>
                  <Td textAlign="right">
                    <HStack justify="flex-end" spacing={1}>
                      {can('deep-link-edit') && (
                        <Tooltip label="Edit"><IconButton aria-label="Edit" icon={<FiEdit2 />} size="xs" variant="ghost" onClick={() => openEdit(row)} /></Tooltip>
                      )}
                      <Menu>
                        <MenuButton as={IconButton} aria-label="More" icon={<FiMoreVertical />} size="xs" variant="ghost" />
                        <MenuList>
                          {can('deep-link-edit') && (
                            <MenuItem icon={row.status_id === 0 ? <FiRotateCcw /> : <FiArchive />} onClick={() => toggleStatus(row)}>
                              {row.status_id === 0 ? 'Restore' : 'Archive'}
                            </MenuItem>
                          )}
                          {can('deep-link-delete') && (
                            <MenuItem icon={<FiTrash2 />} color="red.500" onClick={() => askDelete(row)}>Delete</MenuItem>
                          )}
                        </MenuList>
                      </Menu>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>

        {totalPages > 1 && (
          <Flex justify="flex-end" mt={4} className="pagination-wrap">
            <Pagination
              activePage={page}
              itemsCountPerPage={LIMIT}
              totalItemsCount={total}
              pageRangeDisplayed={5}
              onChange={setPage}
              itemClass="page-item"
              linkClass="page-link"
            />
          </Flex>
        )}
      </BoxWrapper>

      <DeepLinkModal isOpen={drawer.isOpen} onClose={drawer.onClose} initial={editing} onSaved={load} />
      <CsvImportModal isOpen={csv.isOpen} onClose={csv.onClose} onImported={load} />
      <ConfirmDialog
        isOpen={del.isOpen}
        onClose={del.onClose}
        onConfirm={doDelete}
        title="Delete deep link?"
        message={`"${target?.name}" will be removed. Links already copied into templates or Smart Links are unaffected.`}
        confirmLabel="Delete"
      />
    </>
  );
}
